import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../client';
import type {
    LedgerValue,
    PaginatedResponse,
    CreateLedgerValueData,
    UpdateLedgerValueData,
} from '../types';

const PAGE_SIZE = 20;

/**
 * Hook for fetching ledger values with infinite scroll pagination.
 * Uses staleTime to prevent immediate refetch when cache is seeded from batch.
 */
export function useLedgerValues(ledgerId: number) {
    return useInfiniteQuery({
        queryKey: ['ledgers', ledgerId, 'values'],
        queryFn: async ({ pageParam = 0 }) => {
            const params = new URLSearchParams();
            params.append('limit', String(PAGE_SIZE));
            params.append('offset', String(pageParam));
            params.append('order', 'DESC');

            return api.get<PaginatedResponse<LedgerValue>>(
                `/api/ledgers/${ledgerId}/values?${params.toString()}`
            );
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const nextOffset = lastPageParam + PAGE_SIZE;
            // If we've fetched all items, return undefined to signal no more pages.
            if (nextOffset >= lastPage.total) {
                return undefined;
            }
            return nextOffset;
        },
        enabled: !!ledgerId,
        staleTime: 30000, // 30 seconds - prevents refetch when seeded from batch
    });
}

/**
 * Hook for creating a new ledger value.
 * Invalidates the values list cache on success.
 */
export function useCreateLedgerValue(ledgerId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateLedgerValueData) =>
            api.post<LedgerValue>(`/api/ledgers/${ledgerId}/values`, data),
        onSuccess: () => {
            // Invalidate values list to refetch with new data.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId, 'values'] });
            // Also invalidate the ledger itself as latestValue may have changed.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId] });
            // And the ledgers list for the updated latestValue.
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ALL ledger-detail-batch queries so other ledgers see updated userMaxValueDate
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch'] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
        onError: (error, variables) => {
            console.error('[useLedgerValues] Create value failed:', {
                ledgerId,
                data: variables,
                error: error instanceof ApiError ? { status: error.status, data: error.data } : error,
            });
        },
    });
}

/**
 * Hook for updating an existing ledger value.
 * Invalidates the values list cache on success.
 */
export function useUpdateLedgerValue(ledgerId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ valueId, data }: { valueId: number; data: UpdateLedgerValueData }) =>
            api.put<LedgerValue>(`/api/ledgers/${ledgerId}/values/${valueId}`, data),
        onSuccess: () => {
            // Invalidate values list to refetch with updated data.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId, 'values'] });
            // Also invalidate the ledger itself as latestValue may have changed.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId] });
            // And the ledgers list for the updated latestValue.
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ALL ledger-detail-batch queries so other ledgers see updated userMaxValueDate
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch'] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
        onError: (error, variables) => {
            console.error('[useLedgerValues] Update value failed:', {
                ledgerId,
                valueId: variables.valueId,
                data: variables.data,
                error: error instanceof ApiError ? { status: error.status, data: error.data } : error,
            });
        },
    });
}

/**
 * Error code returned when trying to delete the last value of a ledger.
 */
export const LAST_VALUE_ERROR_CODE = 'CANNOT_DELETE_LAST_VALUE';

/**
 * Hook for deleting a ledger value.
 * Invalidates the values list cache on success.
 * Returns error with code when trying to delete the last value.
 */
export function useDeleteLedgerValue(ledgerId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (valueId: number) => {
            try {
                await api.delete<void>(`/api/ledgers/${ledgerId}/values/${valueId}`);
            } catch (error) {
                if (error instanceof ApiError) {
                    // Check if this is the "cannot delete last value" error.
                    const errorData = error.data as { code?: string; error?: string } | null;
                    if (errorData?.code === LAST_VALUE_ERROR_CODE) {
                        throw new Error(
                            errorData.error || 'Cannot delete the last value of a ledger',
                            { cause: error }
                        );
                    }
                }
                throw error;
            }
        },
        onSuccess: () => {
            // Invalidate values list to refetch after deletion.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId, 'values'] });
            // Also invalidate the ledger itself as latestValue may have changed.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId] });
            // And the ledgers list for the updated latestValue.
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ALL ledger-detail-batch queries so other ledgers see updated userMaxValueDate
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch'] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
        onError: (error, valueId) => {
            console.error('[useLedgerValues] Delete value failed:', {
                ledgerId,
                valueId,
                error: error instanceof ApiError ? { status: error.status, data: error.data } : error,
            });
        },
    });
}
